
	import VueModel from "@lib/model/VueModel"
	import Component from "vue-class-component"

	import ContentHeader from "custom/ContentHeader.vue"
	import Page from "custom/Page.vue"
	import IcsButton from "custom/ui/IcsButton.vue"
	import IcsModal from "custom/ui/IcsModal.vue"
	import Loader from "custom/Loader.vue"

	import { GridColumn } from "custom/table/model/grid-column"
	import Grid from "custom/table/Grid.vue"
	import DataView from "custom/table/DataView.vue"
	import FilterSelect from "custom/table/FilterSelect.vue"
	import FilterField from "custom/table/FilterField.vue"

	import { quotationModel, agreementModel } from "store/index"
	import { Agreement } from "store/agreement/types"
	import { quotationModule, agreementModule, authenticationModule } from "store/modules"
	import { CalculationStatus, Quotation, QuotationStatus } from "store/quotation/types"

	import { session } from "api/index"
	import { filterFunctions } from "src/custom/table/utils/filter-functions"

	import { StoreAction } from "@lib/types/vuex"
	import Secured from "utils/security/Secured"
	import { GetterType } from "@lib/model/store/getters"
	import { Mode, Cardinality } from "lib/types/model"
	import ModelProxy from "lib/model/ModelProxy"
	import { Watch } from "vue-property-decorator"

	@Secured(session)
	@Component({
		components: {
			Page,
			ContentHeader,
			DataView,
			Grid,
			FilterSelect,
			FilterField,
			IcsButton,
			IcsModal,
			Loader
		}
	})
	export default class NewBusiness extends VueModel {
		@quotationModule.Getter(GetterType.ITEMS) quotations!: Array<Quotation>
		@agreementModule.Getter(GetterType.ITEMS) agreements!: ReadonlyArray<Agreement>
		@quotationModule.Action deleteQuote!: StoreAction<Quotation>
		@quotationModule.Getter loading!: boolean
		@authenticationModule.Getter loggedIn!: boolean

		readonly baseTranslationKey = "newBusiness"

		agreement: ModelProxy<Agreement, Cardinality.MANY, void> | null = null
		quotation: ModelProxy<Quotation, Cardinality.MANY, void> | null = null

		@Watch("loggedIn", { immediate: true })
		loggedInChanged(): Promise<void> | void {
			if (this.loggedIn) {
				this.agreement = this.attach(agreementModel, Mode.READONLY)
				this.quotation = this.attach(quotationModel, Mode.READONLY, () => {
					if (this.quotation) {
						this.quotation.unfocus()
					}
				})
			}
		}

		filters = filterFunctions

		readonly columns: Array<GridColumn<Quotation>> = [
			{	title: "newBusiness.column.quote", field: "agreementQuoteId", width: "140px" },
			{	title: "newBusiness.column.product", field: "parentAgreement", width: "200px" },
			{	title: "newBusiness.column.customer", field: "company.name", width: "200px", tooltip: row => this.canShowCompany(row), hideTooltipIcon: true },
			{	title: "newBusiness.column.signature", field: "quoteReference", width: "180px", tooltip: row => this.canShowReference(row), hideTooltipIcon: true },
			{	title: "newBusiness.column.lastUpdated", field: "lastUpdated", width: "180px" },
			{	title: "newBusiness.column.status", field: "status", tooltip: row => this.canShowRowTooltip(row) }
		]

		getQuotationStatus(quote: Quotation): string {
			if (quote.status !== QuotationStatus.CONCEPT || !quote.costs) {
				return `${ this.baseTranslationKey }.status.${ quote.status.toLowerCase() }`
			}

			switch (quote.costs.status) {
				case CalculationStatus.CALCULATING:
					return `${ this.baseTranslationKey }.status.calulating`
				case CalculationStatus.CALCULATED:
					return `${ this.baseTranslationKey }.status.finishedCalculation`
				default:
					return `${ this.baseTranslationKey }.status.${ quote.status.toLowerCase() }`
			}
		}

		getAgreementName(id: string): string {
			return this.agreements.find(agreement => agreement.id === id)?.name || ""
		}

		newQuotation(): void {
			if (this.quotation) {
				this.quotation.unfocus()
			}
			this.goToNewQuotationPage()
		}

		isEditable(quotation: Quotation): boolean {
			return ![QuotationStatus.EXPIRED, QuotationStatus.DECLINED, QuotationStatus.FINAL].includes(quotation.status!)
		}

		canBeDeleted(quotation: Quotation): boolean {
			return quotation.status === QuotationStatus.CONCEPT
		}

		continueQuotation(quotation: Quotation): void {
			switch (quotation.status) {
				case QuotationStatus.CONCEPT:
				case QuotationStatus.QUOTE:
					this.focus(quotation.id!)
					this.goToNewQuotationPage()
					break
				case QuotationStatus.REQUEST:
				case QuotationStatus.PENDING:
				case QuotationStatus.FINALIZING:
					this.focus(quotation.id!)
					this.goToRequestProductPage()
					break
				case QuotationStatus.FINAL:
				case QuotationStatus.EXPIRED:
				case QuotationStatus.DECLINED:
					break
				default:
					this.focus(quotation.id!)
					this.goToNewQuotationPage()
					break
			}
		}

		async removeQuotation(quotation: Quotation): Promise<void> {
			const modal = this.$refs.deleteModal as IcsModal

			const canBeDeleted = await modal.show({ data: quotation })
			if (canBeDeleted) {
				await this.deleteQuote(quotation)
			}
		}

		focus(id: string): void {
			if (this.quotation) {
				this.quotation.focus(id)
			}
		}

		goToNewQuotationPage(): void {
			location.href = "new-quotation.html"
		}

		goToRequestProductPage(): void {
			location.href = "request-product.html"
		}

		calculationFailed(quote: Quotation): boolean {
			return quote.costs?.status === CalculationStatus.CALCULATION_FAILED
		}

		calculationSuccess(quote: Quotation): boolean {
			return quote.costs?.status === CalculationStatus.CALCULATED
		}

		truncateValue(string: string, maximumCharacters: number): string {
			return (string.length > maximumCharacters) ? `${ string.slice(0, maximumCharacters - 1) }…` : string
		}

		private canShowReference(quote: Quotation): boolean {
			if (!quote.quoteReference) {
				return false
			}
			return true
		}

		private canShowCompany(quote: Quotation): boolean {
			if (!quote.company?.name) {
				return false
			}
			return true
		}

		private canShowRowTooltip(quote: Quotation): boolean {
			if (quote.status === QuotationStatus.FINALIZING) {
				return true
			}

			const calculated = this.calculationFailed(quote) ||	this.calculationSuccess(quote)
			if (quote.status === QuotationStatus.CONCEPT && calculated) {
				return true
			}

			return false
		}
	}
